// Content data for Fortuna Strive. No fake clients, metrics, testimonials, or awards.
import {
  Compass,
  MonitorSmartphone,
  Workflow,
  Bot,
  MessagesSquare,
  TrendingUp,
  Target,
  Layers,
  Gauge,
  ShieldCheck,
  Users,
  Building2,
  GraduationCap,
  Store,
  UserRound,
} from "lucide-react";

export const capabilities = [
  {
    icon: Target,
    title: "Positioning",
    desc: "Clear positioning that attracts ideal clients and sets you apart.",
  },
  {
    icon: MonitorSmartphone,
    title: "Websites",
    desc: "Conversion-focused websites built to communicate value and generate leads.",
  },
  {
    icon: Workflow,
    title: "Automation",
    desc: "Systems that capture, nurture, and convert leads without manual follow-up.",
  },
  {
    icon: Bot,
    title: "AI Systems",
    desc: "AI-powered workflows that expand capacity and improve response speed.",
  },
];

export const services = [
  {
    slug: "business-strategy",
    icon: Compass,
    title: "Business Strategy",
    short:
      "Positioning, offer development, pricing, business-model clarity, and go-to-market planning.",
    overview:
      "We start with commercial clarity. Before recommending tools or tactics, we define who you serve, what you sell, how you price it, and the sequence of moves that will compound.",
    deliverables: [
      "Positioning and ideal-client definition",
      "Offer architecture and pricing structure",
      "Business-model and revenue mapping",
      "Go-to-market plan and priority roadmap",
    ],
    outcome: "A clear, defensible commercial direction the whole business can execute against.",
  },
  {
    slug: "web-design",
    icon: MonitorSmartphone,
    title: "Web Design & Development",
    short:
      "High-converting corporate websites, landing pages, funnels, and digital experiences built for trust and action.",
    overview:
      "Your website is your most senior salesperson. We design and build digital experiences that communicate value clearly, build trust quickly, and turn attention into qualified enquiries.",
    deliverables: [
      "Corporate websites and landing pages",
      "Conversion-focused funnels",
      "Design systems and reusable components",
      "Performance, accessibility, and SEO foundations",
    ],
    outcome: "A credible digital presence engineered to convert the right visitors.",
  },
  {
    slug: "marketing-automation",
    icon: Workflow,
    title: "Marketing Automation",
    short:
      "CRM architecture, lead capture, email and SMS follow-up, pipeline automation, and reporting systems.",
    overview:
      "Most leads are lost in the gaps between tools. We architect the CRM, wire up capture and follow-up, and build the pipeline automation that keeps every opportunity moving.",
    deliverables: [
      "CRM architecture and pipeline design",
      "Lead capture and routing",
      "Email and SMS follow-up sequences",
      "Reporting and performance dashboards",
    ],
    outcome: "A connected system that captures, nurtures, and converts without manual chasing.",
  },
  {
    slug: "ai-systems",
    icon: Bot,
    title: "AI & Systems",
    short:
      "AI voice agents, conversational workflows, integrations, and intelligent business automation.",
    overview:
      "AI expands capacity when it is applied to the right workflows. We implement intelligent agents and integrations that improve response speed and remove repetitive work.",
    deliverables: [
      "AI voice and conversational agents",
      "Workflow and process automation",
      "Systems integration across tools",
      "Guardrails, monitoring, and handover logic",
    ],
    outcome: "More capacity and faster response, without adding headcount.",
  },
  {
    slug: "brand-messaging",
    icon: MessagesSquare,
    title: "Brand & Messaging",
    short:
      "Brand positioning, messaging systems, offer communication, and customer-facing content.",
    overview:
      "Clarity converts. We build the messaging system that makes your offer easy to understand, easy to trust, and easy to say yes to across every customer touchpoint.",
    deliverables: [
      "Messaging framework and voice",
      "Offer and value communication",
      "Customer-facing content structure",
      "Sales and website copy alignment",
    ],
    outcome: "A consistent message that makes the value of your work obvious.",
  },
  {
    slug: "growth-advisory",
    icon: TrendingUp,
    title: "Growth Advisory",
    short:
      "Ongoing strategic support to identify bottlenecks, improve execution, and scale more predictably.",
    overview:
      "Growth is a sequence of good decisions. We provide ongoing advisory to identify bottlenecks, prioritise the right work, and improve how the business executes over time.",
    deliverables: [
      "Regular strategic reviews",
      "Bottleneck and priority analysis",
      "Execution and accountability support",
      "Scaling and operating guidance",
    ],
    outcome: "A steady hand on strategy so the business scales predictably.",
  },
];

export const approachSteps = [
  {
    number: "01",
    title: "Clarify the Offer",
    icon: Target,
    desc: "We clarify positioning, target market, offer structure, customer journey, and the commercial priorities that matter most.",
  },
  {
    number: "02",
    title: "Build the Infrastructure",
    icon: Layers,
    desc: "We build the website, funnel, CRM, workflows, reporting systems, and sales infrastructure required for execution.",
  },
  {
    number: "03",
    title: "Automate Follow-Up",
    icon: Workflow,
    desc: "We implement automation that captures leads, nurtures opportunities, improves response time, and removes repetitive work.",
  },
  {
    number: "04",
    title: "Improve Operations",
    icon: Gauge,
    desc: "We refine internal systems, monitor performance, identify bottlenecks, and improve how the business runs.",
  },
];

export const whoWeWorkWith = [
  { icon: UserRound, title: "Consultants" },
  { icon: Users, title: "Agencies" },
  { icon: GraduationCap, title: "Education Brands" },
  { icon: Store, title: "Local Service Businesses" },
  { icon: Building2, title: "Founder-Led Companies" },
];

export const credibility = [
  {
    icon: Compass,
    title: "Strategy First",
    desc: "We begin with business clarity before recommending tools or tactics.",
  },
  {
    icon: Layers,
    title: "Systems Driven",
    desc: "We build connected infrastructure designed for repeatable execution.",
  },
  {
    icon: TrendingUp,
    title: "Results Focused",
    desc: "We focus on measurable operational and commercial outcomes.",
  },
  {
    icon: ShieldCheck,
    title: "Private & Professional",
    desc: "A registered Indian private limited company serving businesses across multiple markets.",
  },
];

export const frameworkStages = [
  {
    number: "01",
    title: "The Offer Has Not Proven Itself",
    focus: [
      "Unclear market demand",
      "Weak positioning",
      "Inconsistent customer response",
      "No repeatable acquisition channel",
    ],
    objective: "Validate the offer before scaling activity.",
  },
  {
    number: "02",
    title: "The Offer Works, but the Business Does Not",
    focus: [
      "Founder-dependent sales",
      "Inconsistent delivery",
      "Manual follow-up",
      "Disconnected tools",
      "Weak reporting",
    ],
    objective: "Turn isolated wins into a repeatable business system.",
  },
  {
    number: "03",
    title: "The Founder Steps Back and the Business Holds",
    focus: [
      "Clear responsibilities",
      "Documented processes",
      "Reliable systems",
      "Management visibility",
      "Consistent execution",
    ],
    objective: "Build operational independence.",
  },
  {
    number: "04",
    title: "The Business Runs and the Founder Designs",
    focus: [
      "Strategic leadership",
      "Scalable teams",
      "Data-led decision-making",
      "Expansion, innovation, capital allocation",
    ],
    objective: "Move the founder from operator to architect.",
  },
];

export const serviceOptions = [
  "Business Strategy",
  "Web Design & Development",
  "Marketing Automation",
  "AI & Systems",
  "Brand & Messaging",
  "Growth Advisory",
  "Not sure yet",
];

export const contactMethods = ["Email", "Phone", "Either"];

// Approved client names only. Reusable structure — logo, industry, and link
// fields can be added later without redesigning the section.
export const clients = [
  { name: "Bounce City 321 LLC", label: "Client" },
  { name: "J11 Consulting LLC", label: "Client" },
  { name: "Pure Floor Restore LLC", label: "Client" },
  { name: "HNM Consultancy LLC", label: "Client" },
  { name: "Nexlearn Consulting LLC", label: "Client" },
  { name: "Dapper Codes LLC", label: "Client" },
  { name: "Prestige Resin LLC", label: "Client" },
  { name: "Tulsa Premier Cleaning Services", label: "Client" },
  { name: "Elite Carpet Cleaning Pros LLC", label: "Client" },
  { name: "AARVA — The Aroma of South", label: "Client" },
  { name: "Organic Carpet Cleaning", label: "Client" },
  { name: "Clean Air Carpet Care", label: "Client" },
  { name: "Lynx Coating LLC", label: "Client" },
];
